import React, { useEffect, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { CheckCircle, AlertTriangle, Loader2, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';

export default function VerifyEmailPage() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const { verifyEmail, isAuthenticated } = useAuth();
  const token = sp.get('token') || '';
  const [status, setStatus] = useState('loading'); // loading | success | error
  const [stripeClaimed, setStripeClaimed] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      if (!token) {
        setStatus('error');
        setError('This confirmation link is missing information.');
        return;
      }
      try {
        const data = await verifyEmail(token);
        setStripeClaimed(data?.stripe_claimed === true);
        setStatus('success');
      } catch (err) {
        setStatus('error');
        setError(err.message || 'Could not confirm your email.');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (status === 'loading') {
    return (
      <div className="max-w-xl mx-auto py-10 text-center">
        <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto mb-4" />
        <p className="text-lg text-text-muted">Confirming your email…</p>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="max-w-xl mx-auto py-4 fade-in">
        <div className="bg-white rounded-3xl border-2 border-success/30 shadow-sm p-8 md:p-10 text-center">
          <div className="w-16 h-16 bg-success/10 rounded-full mx-auto mb-4 flex items-center justify-center">
            <CheckCircle className="w-8 h-8 text-success" />
          </div>
          <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-text mb-3">Email confirmed</h1>
          {stripeClaimed ? (
            <div className="bg-secondary/10 border-2 border-secondary/40 rounded-xl p-5 mb-6">
              <Sparkles className="w-6 h-6 text-secondary mx-auto mb-2" />
              <p className="text-text font-semibold text-lg">
                We found your purchase and unlocked the full course for you.
              </p>
            </div>
          ) : (
            <p className="text-text-muted text-lg mb-6">You can now save your progress across all your devices.</p>
          )}
          <Button onClick={() => navigate(isAuthenticated ? '/' : '/login')} className="rounded-xl py-5 px-8 text-lg font-bold">
            Continue
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto py-4 fade-in">
      <div className="bg-white rounded-3xl border-2 border-error/30 shadow-sm p-8 md:p-10 text-center">
        <div className="w-16 h-16 bg-error/10 rounded-full mx-auto mb-4 flex items-center justify-center">
          <AlertTriangle className="w-8 h-8 text-error" />
        </div>
        <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-text mb-3">Couldn't confirm your email</h1>
        <p className="text-text-muted text-lg mb-6">{error}</p>
        <Link to={isAuthenticated ? '/' : '/login'}>
          <Button variant="outline" className="rounded-xl py-5 px-8 text-lg font-bold">Back to {isAuthenticated ? 'home' : 'sign in'}</Button>
        </Link>
      </div>
    </div>
  );
}
